import { cn } from '@morpho-org/ui-v6/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

/**
 * TableNoResults card variants
 */
const tableNoResultsVariants = cva(
  ['gap-sm flex flex-col items-center justify-center', 'w-full rounded-lg'],
  {
    variants: {
      withBackground: {
        true: 'bg-card',
        false: 'bg-transparent'
      },
      withBorder: {
        true: 'border-[0.5px] border-border',
        false: ''
      }
    },
    defaultVariants: {
      withBackground: true,
      withBorder: false
    }
  }
)

export interface TableNoResultsProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof tableNoResultsVariants> {
  /** Message to display */
  text?: string
  /** Height of the container */
  height?: string
  /** Additional content to render below the text */
  children?: ReactNode
}

/**
 * TableNoResults component - Tailwind replica of dapp-ui-v5 TableNoResults
 *
 * A component to display when a table has no results to show.
 * Renders a centered message in a card layout.
 *
 * @example
 * ```tsx
 * <TableNoResults />
 * <TableNoResults text="No matching records found" height="300px" />
 * <TableNoResults withBorder withBackground={false}>
 *   <Button>Add Item</Button>
 * </TableNoResults>
 * ```
 */
const TableNoResults = forwardRef<HTMLDivElement, TableNoResultsProps>(
  (
    {
      text = 'No results found.',
      height = '250px',
      className,
      children,
      withBorder,
      withBackground,
      style,
      ...props
    },
    ref
  ) => {
    return (
      <div className="gap-sm flex w-full flex-col">
        <div
          ref={ref}
          className={cn(tableNoResultsVariants({ withBackground, withBorder }), className)}
          style={{ height, ...style }}
          {...props}
        >
          <span className="text-2xs text-muted-foreground">{text}</span>
          {children}
        </div>
      </div>
    )
  }
)

TableNoResults.displayName = 'TableNoResults'

export { TableNoResults, tableNoResultsVariants }
